import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { Subject, debounceTime, filter, switchMap, takeUntil } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../../utils/src/lib/services/api-url.service';
import { SearchMode } from '../../../interfaces/mapSearch.interface';
import { ISearchConfig } from '../../../models/config-mappa.model';
import { FeatureSelectService } from '../../../services/feature-select.service';
import { MapService } from '../../../services/map.service';
import { MapSearchService } from './map-search.service';

@Component({
  selector: 'ebw-map-search',
  templateUrl: './map-search.component.html',
  styleUrls: ['./map-search.component.scss']
})
export class MapSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  public DEFAULT_ACTION = 'search';
  @Input() public config: ISearchConfig;
  @Input() public isGoogleKeyEnabled: boolean;
  @ViewChild('MapsAutocomplete') autocompleteInput: ElementRef<HTMLInputElement>;

  autocompleteElement: google.maps.places.Autocomplete = null;
  public autoCompletefilteredOptions: any[] = [];

  public activeSearchModeIdx = 0;
  public currentFeatureResponse;

  public searchModes: SearchMode[] = [];

  public inputVal = '';
  public inputValLength = 1;
  public inputDefaultVal = '';

  public isLoading = false;

  private _customTerm$: Subject<string> = new Subject();
  private _defaultTerm$: Subject<string> = new Subject();
  private destroy$ = new Subject<void>();

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    public mapService: MapService,
    private ebwModalService: EbwModalService,
    private featureSelectService: FeatureSelectService,
    private mapSearchService: MapSearchService,
    private utilsService: UtilsService,
    private _apiUrlService: ApiUrlService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    // se non ci sono le config esco
    if (!this.config) {
      console.error('[MapSearchComponent][ngOnInit] config assenti');
      return;
    }

    // inizializzo le modalità di ricerca
    this.searchModes = this.mapSearchService.getSearchModes(this.config.modes, this.isGoogleKeyEnabled);

    // TODO sta cosa serve ancora??
    // const userLayerPermission = this.userManagerService.userPermissions.find((permission) => {
    //   return permission.startsWith('VISIBILITY_LAYER_');
    // });

    // TODO sta cosa serve ancora??
    // if (!userLayerPermission) {
    this.utilsService.getRequest(this._apiUrlService.createUrl(API_URLS.PERMISSION.LIST), {}).subscribe(
      (result) => {
        let layerVisibilityPermission = result.find((permission) => {
          return permission.name.startsWith('VISIBILITY_LAYER_');
        });

        if (layerVisibilityPermission) {
          let ricercaAssetsIndex = this.searchModes.findIndex((mode) => {
            return mode.label == 'Ricerca assets';
          });

          if (ricercaAssetsIndex > -1) {
            this.searchModes.splice(ricercaAssetsIndex, 1);
          }
        }
      },
      (error) => {
        this.ebwModalService.openErrorModal({
          title: 'common.error',
          content: 'Could not retrieve permissions'
        });
      }
    );
    //}

    // mi metto in ascolto sulla digitazione in searchbar
    this.listenOnSearchTyping();
  }

  // --------------------------------------------------------------------------------------------------------------------

  ngAfterViewInit(): void {
    // RICERCA GOOGLE
    this.mapService.readyGoogleMapsMapChanged.subscribe({
      next: (isReady) => {
        if (isReady && this.isGoogleKeyEnabled) {
          // definisco i campi delle opzioni che voglio recuperare
          const fields = ['name', 'formatted_address', 'icon', 'geometry'];
          // definisco il limite su cui voglio permettere la ricerca
          const componentRestrictions = { country: 'it' };
          const options: google.maps.places.AutocompleteOptions = { componentRestrictions, fields };
          this.autocompleteElement = new google.maps.places.Autocomplete(this.autocompleteInput.nativeElement, options);
          // Mi metto in ascolto sulle variazioni del campo input di google
          this.autocompleteElement.addListener('place_changed', (val) => {
            this.centerMapFromGooglePlaceResult(this.autocompleteElement, options);
          });
        }
      }
    });
  }

  // --------------------------------------------------------------------------------------------------------------------

  centerMapFromGooglePlaceResult(autocompleteElement: google.maps.places.Autocomplete, options: google.maps.places.AutocompleteOptions) {
    // Recupero info sul record selezionato
    const place: google.maps.places.PlaceResult = autocompleteElement.getPlace();
    // se trovo la geometria
    if (place.geometry) {
      // Aggiorno il valore mostrato a video nella barra di ricerca
      this.inputDefaultVal = this.autocompleteInput.nativeElement.value;
      this.mapSearchService.centerMapOnPoint(place.geometry);
      return;
    }
    // alrimenti se sono entrato qui vuol dire che l'utente ha premuto invio prima di selezionare un'opzione dalla tendina
    const autocompleteService = new google.maps.places.AutocompleteService();
    // basandomi sul valore contenuto nella barra di ricerca recupero manualmente le opzioni
    autocompleteService.getPlacePredictions(
      { ...options, input: this.autocompleteInput.nativeElement.value },
      (placesList: google.maps.places.AutocompletePrediction[]) => {
        // Prendo il primo risultato per simularne un auto selezione
        const firstPlace = placesList[0];
        // Se entro qui vuol dire che nella barra di ricerca o c'è sporcizia, o l'utente ha ricercato delle coordinate
        // per ora non faccio niente
        if (!firstPlace) {
          return;
        }
        // Aggiorno il valore mostrato a video nella barra di ricerca
        this.inputDefaultVal = firstPlace.description;
        // Recupero i dettagli del luogo
        const placesService = new google.maps.places.PlacesService(this.autocompleteInput.nativeElement);
        placesService.getDetails({ ...options, placeId: firstPlace.place_id }, (place) => this.mapSearchService.centerMapOnPoint(place.geometry));
      }
    );
  }

  // --------------------------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --------------------------------------------------------------------------------------------------------------------

  onSearchInputChange(value) {
    this.inputValLength = typeof value === 'string' ? value.length : value.desc.length;
    this._customTerm$.next(value);
  }

  // --------------------------------------------------------------------------------------------------------------------

  onChangeSearchMode(event: MatButtonToggleChange) {
    this.activeSearchModeIdx = event.value;
    this.clearDefaultSearchVal();
    this.clearAutoComplete();
  }

  // --------------------------------------------------------------------------------------------------------------------

  clearDefaultSearchVal() {
    this.autocompleteInput.nativeElement.value = '';
    this.inputDefaultVal = null;
    this.mapService.removeMarker(this.mapSearchService.markerTitle);
  }

  // --------------------------------------------------------------------------------------------------------------------

  clearAutoComplete() {
    this.inputVal = null;
    this.inputValLength = null;
    this.autoCompletefilteredOptions = null;
    // clear the current feature from being displayed
    this.featureSelectService.clearCurrentFeature(true);
    this.currentFeatureResponse = null;
  }

  // --------------------------------------------------------------------------------------------------------------------

  defaultInputChange(val) {
    this._defaultTerm$.next(val);
  }

  // --------------------------------------------------------------------------------------------------------------------

  displayFn(obj: any): string {
    return obj ? obj.desc : '';
  }

  // --------------------------------------------------------------------------------------------------------------------

  async onAutocompleteSelectionChanged(event: MatAutocompleteSelectedEvent) {
    const val = event.option.value;
    // cerco un asset tramite il suo urn
    const response = await this.mapSearchService.onAutocompleteSelectionChanged(val.urns);
    // se non lo trovo esco
    if (!response) {
      return;
    }
    this.currentFeatureResponse = response;
  }

  // --------------------------------------------------------------------------------------------------------------------

  showFeaturePopup = () => this.mapSearchService.handleSearchItemSelection(this.currentFeatureResponse);

  // --------------------------------------------------------------------------------------------------------------------

  private listenOnSearchTyping() {
    this._customTerm$
      .pipe(
        takeUntil(this.destroy$),
        filter((value: string) => value.length >= 3),
        debounceTime(600),
        switchMap((val) => {
          this.isLoading = true;
          const filterBy = this.searchModes[this.activeSearchModeIdx].filterKey;
          return this.mapSearchService.getAutocompleteOpts(val, filterBy);
        })
      )
      .subscribe((res) => {
        this.isLoading = false;
        this.autoCompletefilteredOptions = res;
      });

    this._defaultTerm$
      .pipe(
        takeUntil(this.destroy$),
        filter((value: string) => value.length >= 3),
        debounceTime(600)
      )
      .subscribe({
        next: (val) => {
          if (this._isValidCoordinates(val)) {
            this._setCenterFromCoordinates(val);
          } else {
            this.inputDefaultVal = val;
          }
        }
      });
  }

  private _setCenterFromCoordinates(val) {
    const latlngStr = val.trim().split(/[,\s]+/);

    const latlng = [parseFloat(latlngStr[1]), parseFloat(latlngStr[0])];

    this.mapService.addMarker('PLACES_MARKER', latlng);
    this.mapService.setMapCenter(latlng);
  }

  private _isValidCoordinates(str) {
    const regex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)[,\s]+[-+]?((1[0-7]\d|[1-9]?\d)(\.\d+)?|180(\.0+)?)$/;
    return regex.test(str);
  }
}
